import { generateAddress } from '@iota/core'
import { Response } from '../request'
import { generateBundle, generateSign } from '../tools'
import { addChecksum } from '@iota/checksum'

import BaseController from '../base'

export interface AddressList {
  newAddressList: [string]
  lastAddress: string
}

export interface AddressResponse {
  isTransaction: number
  endIndex: number
  totalSize: number
  addresses: Array<Addresses>
}

export interface Addresses {
  index: number
  address: string
}

export interface AccountInfoResponse extends Response {
  data: AddressResponse
}

export interface Address {
  (seed: string, index: number): string
}

export default class UserAddress extends BaseController {
  private addressListPageSize = 500

  genAddress: Address = (seed, index) => {
    return generateAddress(seed, index)
  }

  getAddressList = async (seed: string) => {
    try {
      let firstAddress = this.genAddress(seed, 0)
      let res = await this.getAccountInfoByFirstAddress(seed, firstAddress)

      if (res.isTransaction) {
        let oldAddress = res.addresses.map(item => item.address)
        let resp = await this.generateNewAddressAndSaveAddress(
          seed,
          firstAddress,
          res.endIndex + 1,
          oldAddress
        )
        let addressList = resp.addresses.map(item => item.address)
        let lastAddress = addressList[resp.endIndex]
        let lastAddress90 = addChecksum(lastAddress)
        return {
          newAddressList: addressList,
          lastAddress: lastAddress90
        }
      } else {
        let addressList = res.addresses.map(item => item.address)
        let lastAddress = addressList[res.endIndex]
        let lastAddress90 = addChecksum(lastAddress)
        return {
          newAddressList: addressList,
          lastAddress: lastAddress90
        }
      }
    } catch (error) {
      throw error
    }
  }

  getAccountInfoByFirstAddress = async (
    seed: string,
    firstAddress: string,
    currentPage: number = 1,
    isLockRepeat?: boolean
  ): Promise<AddressResponse> => {
    try {
      let params = {
        firstAddress,
        currentPage: currentPage + '',
        pageSize: this.addressListPageSize + ''
      }
      let { status, data, msg } = await this.post('api/getAccountInfoByFirstAddress', params)
      if (status === '1') {
        // 查询到地址列表
        const { addresses, endIndex, totalSize, isTransaction } = data
        // 要重复获取几次数据
        let times = Math.floor(totalSize / this.addressListPageSize)
        // 如果要获取的次数大于等于当前页数 继续重复获取
        if (!isLockRepeat && times >= currentPage) {
          let restValues = await this.repeatGetAccountInfoByFirstAddress(
            seed,
            firstAddress,
            totalSize
          )
          restValues.forEach(item => {
            item.forEach(i => {
              addresses.push(i)
            })
          })
          return {
            isTransaction,
            endIndex,
            totalSize,
            addresses: addresses
          }
        } else {
          // 如果一页能获取完，直接把数据返回
          return data
        }
      } else if (status === '-2') {
        return this.generateNewAddressAndSaveAddress(seed, firstAddress, 0)
      } else {
        throw new Error(msg)
      }
    } catch (error) {
      throw error
    }
  }

  saveAddress = (firstAddress: string, newAddress: string, index: number, seed: string) => {
    let bundle = generateBundle(newAddress)
    let signatureFragment = generateSign(bundle, 0, 2, seed)
    let params = {
      firstAddress,
      newAddress,
      index,
      bundle,
      signatureFragment
    }
    return this.post('api/saveAddress', params)
  }

  repeatGetAccountInfoByFirstAddress = async (
    seed: string,
    firstAddress: string,
    totalSize: number
  ): Promise<Addresses[][]> => {
    let times = Math.floor(totalSize / this.addressListPageSize) // 要重复获取几次数据
    let getAccountInfoByFirstAddressArray = []
    for (let i = 2; i < times + 2; i++) {
      getAccountInfoByFirstAddressArray.push(
        //  getAccountInfoByFirstAddress 方法
        () => this.getAccountInfoByFirstAddress(seed, firstAddress, i, true)
      )
    }
    let values = await Promise.all(getAccountInfoByFirstAddressArray.map(item => item()))
    return values.map(item => item.addresses)
  }

  generateNewAddressAndSaveAddress = async (
    seed: string,
    firstAddress: string,
    index: number,
    addressList: Array<string> = []
  ): Promise<AddressResponse> => {
    let newAddress = this.genAddress(seed, index)
    let saveRes = await this.saveAddress(firstAddress, newAddress, index, seed)
    if (saveRes.status === '1') {
      addressList.push(newAddress)
      let res = this.generateNewAddressAndSaveAddress(seed, firstAddress, ++index, addressList)
      return res
    } else if (saveRes.status === '0') {
      addressList.push(newAddress)
      let addressesObjs = addressList.map((item, index) => {
        return {
          index,
          address: item
        }
      })
      return {
        isTransaction: 0,
        endIndex: addressList.length - 1,
        totalSize: addressList.length,
        addresses: addressesObjs
      }
    } else {
      throw new Error('Save address error')
    }
  }
}
